/**
 * Where a notification should take you when it is clicked.
 *
 * Every alert the agent can click — a Web Push banner from the service worker,
 * a desktop notification from the open tab, an in-app toast — is about one
 * thing in one tenant, and clicking it should land on exactly that. Getting
 * there needs *both* ids: an agency console holds several apps, and a
 * conversation id means nothing until the right app is selected, so a link
 * carrying only `c=` drops the agent on whichever tenant happened to be in
 * focus and an empty thread.
 *
 * The mapping lives here, alone, because four call sites were each deciding it
 * for themselves and disagreeing.
 */

/** Push and toast payloads name a ticket this way. */
const isTicket = (payload) => payload?.kind === 'ticket' || (!payload?.conversationId && payload?.ticketId);

/**
 * @param {object} payload  anything carrying `appId` and `conversationId`
 * @returns {string} a dashboard path, always absolute, never empty
 */
export function notificationRoute(payload = {}) {
  const params = new URLSearchParams();
  // The tenant first: ChatView switches app before resolving the conversation,
  // so one click lands on the right thread rather than an empty list.
  if (payload.appId) params.set('app', String(payload.appId));

  if (isTicket(payload)) {
    // Tickets are not addressable by id in the UI yet; the app is what makes
    // the list the right one.
    const query = params.toString();
    return query ? `/tickets?${query}` : '/tickets';
  }

  if (payload.conversationId) params.set('c', String(payload.conversationId));

  const query = params.toString();
  return query ? `/chat?${query}` : '/chat';
}

export default { notificationRoute };
